<template>
  <div
    id="chat"
    class="bg-transparent pt-1 px-1 relative flex flex-col desktop:max-w-sm"
    :class="{
      'content-end': !showQuestions,
      'grid-rows-[1fr_auto]': showQuestions,
    }"
    v-bind="$attrs"
    @click.prevent.stop
  >
    <slot name="top"></slot>

    <!--
      NO flex `gap` and NO extra wrapper spacing: vue-virtual-scroller is blind
      to flex gap and measures only each item's own height. Per-row spacing is
      ChatMessage's own py-2.5 (inside the measured item) — the single
      deterministic source. `scrollerBuffer` full-measures the loaded set so
      the library's post-fling scroll-anchor correction never runs; integer-px
      line-heights in ChatMessage keep multi-line rows pixel-stable. min-item-
      size is a first-paint-only floor (full-measure makes it moot after).
    -->
    <DynamicScroller
      ref="messageContainer"
      :items="messages"
      :min-item-size="80"
      :buffer="scrollerBuffer"
      class="flex flex-col py-1 overflow-y-auto h-full max-height-full chat-dynamic-scroller"
      data-message-container="true"
    >
      <template #before>
        <!--
          Only the pinned-message banner is sticky (stays visible while
          scrolling history). The sticky was previously applied to the whole
          scroller `:first-child` slot, which also contains the load-more strip
          below — pinning it too. Scoping sticky to just this banner keeps the
          load-more / loading / "no more" strip in normal flow so it scrolls
          with the messages instead of floating.
        -->
        <button
          v-if="chat.pinnedMessage"
          class="sticky top-0 z-10 flex w-full items-center gap-0.5 rounded-[5px] bg-fill-brand-secondary px-3 py-2 text-left font-sans text-sm font-semibold leading-5 text-text backdrop-blur-[35px]"
          data-button="true"
          @click="scrollIntoMessage(chat.pinnedMessage.id)"
        >
          <div class="flex flex-col items-start gap-0.5 flex-1">
            <span class="text-sm font-semibold leading-5">
              {{ $t("channel.chat.pinnedMessage") }}
            </span>
            <div class="flex items-start gap-2 self-stretch">
              <Avatar :user="chat.pinnedMessage.author" size="xs" />
              <span class="self-stretch text-[14px] font-medium leading-[20px]">
                <ChatMessageText
                  :text="pinnedMessageContent ?? ''"
                  :interactive="false"
                />
              </span>
            </div>
          </div>
          <button
            v-if="channelStore.permissions.manageChannel"
            class="w-6 h-6"
            @click="onSetMessagePinnedState(chat.pinnedMessage.id, false)"
          >
            <Icon icon="mdi:close" class="w-6 h-6 text-text-brand" />
          </button>
        </button>

        <!--
          Load-more control. Older history is prefetched automatically by a
          throttled scroll handler (see script) before the top is reached; this
          is also a real, tappable fallback and the status affordance. Constant
          height across "Load more" / loading / "no more" so transitions never
          shift layout and captureScrollAnchor keeps the read position pinned
          across each prepend. a11y: a genuine <button> with an accessible
          label and aria-busy while loading; the end state is a role="status"
          aria-live region so screen readers announce reaching the start.
        -->
        <div
          v-if="showHistoryControl"
          class="flex h-12 shrink-0 items-center justify-center px-3"
        >
          <button
            v-if="fetchingMoreMessages || (hasOlderMessages && !reachedStart)"
            type="button"
            class="flex h-9 items-center gap-2 rounded-full bg-bg-secondary px-4 text-sm font-medium text-text-secondary transition-colors hover:text-text focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand-primary disabled:cursor-default disabled:opacity-80"
            :disabled="fetchingMoreMessages"
            :aria-busy="fetchingMoreMessages"
            @click="loadPreviousMessages"
          >
            <span
              v-if="fetchingMoreMessages"
              class="loading loading-spinner loading-sm shrink-0"
              aria-hidden="true"
            />
            <Icon
              v-else
              icon="mdi:arrow-up"
              class="size-4 shrink-0"
              aria-hidden="true"
            />
            <span>{{
              fetchingMoreMessages
                ? $t("channel.chat.loadingMessages")
                : $t("channel.chat.loadMoreMessages")
            }}</span>
          </button>
          <div
            v-else
            role="status"
            aria-live="polite"
            class="flex h-9 items-center gap-2 px-4 text-sm font-medium text-text-tertiary"
          >
            <Icon
              icon="mdi:check-circle-outline"
              class="size-4 shrink-0"
              aria-hidden="true"
            />
            <span>{{ $t("channel.chat.noMoreMessages") }}</span>
          </div>
        </div>
      </template>
      <template #default="{ item, index, active }">
        <DynamicScrollerItem
          :item="item"
          :active="active"
          :data-index="index"
          :size-dependencies="[
            item.content,
            item.messageTranslation,
            !!item.replyTo,
            item.isVisible,
          ]"
        >
          <ChatMessage
            v-if="item.isVisible"
            :is-mobile="false"
            :message="item"
            :is-liked="likes?.[item.id]?.isLiked ?? false"
            :likes="likes?.[item.id]?.likes ?? 0"
            :is-reply="!!item.replyTo"
            :is-replying="!!replyToData"
            :is-replying-to-message="
              !!replyToData && item.id === replyToData?.message?.id
            "
            :is-replying-to-upstream="
              !!replyToData && item.replyTo?.id === replyToData?.message?.id
            "
            @parent-reply="onReplyPress"
            @reply="onReplyPress"
            @like="onMessageLike"
            @delete="onDeleteMessage"
            @set-pinned-state="onSetMessagePinnedState"
          />
        </DynamicScrollerItem>
      </template>
    </DynamicScroller>

    <Button
      v-if="isScrollingManually"
      variant="filled"
      class="absolute bottom-44 right-2 z-10 backdrop-blur-3xl ml-auto"
      :class="replyToData !== null ? 'bottom-[5.625rem]' : 'bottom-[5rem]'"
      @click="() => scrollToBottom()"
    >
      {{ $t("channel.chat.moreMessages") }}
      <Icon icon="mdi:arrow-down" class="text-lg" />
    </Button>

    <div
      class="absolute bottom-12 z-[999999999] w-full max-w-sm px-0 flex flex-col gap-2 justify-center"
    >
      <Toast
        v-for="[id, toast] in chatToasts"
        :key="id"
        :message="toast.message"
        :type="toast.type"
        :title="toast.title"
        :persistent="toast.persistent"
        :icon="toast.icon"
        @close="() => removeToast(id)"
      />
    </div>

    <ChatQuestionField
      v-if="showQuestions"
      v-model="newMessage"
      :keyboard-open="true"
      @submit="sendMessage"
      @go-to-upstream="scrollIntoMessage"
      @close-questions="showQuestions = false"
    />

    <ChatMessageField
      v-else
      v-model:message="newMessage"
      :reply-to-message="replyToData?.message ?? null"
      @submit="sendMessage"
      @clear-reply="replyToData = null"
      @go-to-upstream="scrollIntoMessage"
    >
      <template #right>
        <div
          v-if="allowQuestions && !replyToData?.message"
          class="flex justify-end"
        >
          <button
            class="flex w-[3.125rem] h-[3.125rem] !min-w-10 !min-h-10 justify-center items-center gap-2 rounded-full bg-fill-brand-secondary backdrop-blur-[17.5px]"
            aria-label="Filter questions"
            @click="showQuestions = true"
          >
            <Icon
              icon="mdi:question-box-multiple"
              class="w-5 h-5 text-lg text-basics-primary"
              aria-hidden
            />
          </button></div
      ></template>
    </ChatMessageField>
  </div>

  <DeleteMessageConfirmationModal
    v-model:is-open="deleteMessageConfirmationModalOpen"
    @accept="deleteMessage()"
    @decline="deleteMessageConfirmationModalOpen = false"
  />
</template>

<script setup lang="ts">
import {
  ref,
  watch,
  watchEffect,
  nextTick,
  computed,
  useTemplateRef,
  type ComponentPublicInstance,
  onMounted,
} from "vue";
import ChatMessage from "./chat-message.vue";
import ChatMessageField from "./chat-message-field.vue";
import ChatMessageText from "./chat-message-text";
import { useDebounceFn } from "@vueuse/core";
import Button from "../ui/button/button.vue";
import { Icon } from "@iconify/vue";
import { getClosestScrollableParent } from "@/utils/misc";
import { captureScrollAnchor } from "@/utils/scroll-anchor";
import { useToastStore } from "@/stores/toast.store";
import Toast from "@/components/ui/toast/toast.vue";
import DeleteMessageConfirmationModal from "@/components/show/modals/delete-message-confirmation-modal.vue";
import ChatQuestionField from "./chat-question-field.vue";
import { useChat } from "@/stores/chat/services/use-chat";
import type { IChatMessage } from "@/stores/chat/model/chat-model";
import Avatar from "../me/avatar.vue";
import { useChannelStore } from "@/stores/channel.store";
import {
  useLocalAutoTranslate,
  useObjectTranslation,
} from "@/stores/translation.store";

interface MessageReplyData {
  message: IChatMessage;
  replyToId: string;
}

const props = defineProps<{
  allowQuestions: boolean;
  loadMessagesOnMount?: boolean;
}>();

const emit = defineEmits<{
  sendMessage: [message: string, isQuestion: boolean, replyToId: string | null];
  messageLike: [messageId: string, isLiked: boolean];
}>();

const chat = useChat();
const channelStore = useChannelStore();

const { chatToasts, removeToast } = useToastStore();
const replyToData = ref<MessageReplyData | null>(null);
const deleteMessageConfirmationModalOpen = ref<boolean>(false);
const toDeleteMessageId = ref<string | null>(null);
const showQuestions = ref<boolean>(false);
const messages = computed(() =>
  showQuestions.value ? chat.questions : Array.from(chat.messages ?? []),
);

const likes = computed(() => chat.likes?.messagesLikesMap);

// Buffer sized to exceed the FULL loaded set's content height so the scroller
// renders + measures every loaded message up front. With no unmeasured region
// the size estimate→measure→compensate cycle never runs mid-scroll, so a fast
// fling cannot produce drift. The loaded window is bounded by chat pagination,
// so this is the same DOM cost as a plain list. 600px is a safe per-message
// upper bound (observed max ≈340px); over-estimating only renders more.
const scrollerBuffer = computed(() =>
  Math.max(2000, messages.value.length * 600),
);

const localAutoTranslate = useLocalAutoTranslate();

const { text: pinnedMessageContent } = useObjectTranslation(
  computed(() => {
    if (!chat.pinnedMessage) return null;
    return {
      authorUserId: chat.pinnedMessage.author.id,
      originalText: chat.pinnedMessage.content,
      objectAccessors: {
        objectId: chat.pinnedMessage.id,
        objectProperty: "content",
      },
      translatedText: chat.pinnedMessage.messageTranslation,
      localAutoTranslate: localAutoTranslate.value,
    };
  }),
);

const fetchingMoreMessages = ref(false);
const hasOlderMessages = computed(() =>
  chat.hasMoreMessagesForRoom(chat.resolvedChatRoom?.id),
);
const showHistoryControl = computed(
  () =>
    fetchingMoreMessages.value || hasOlderMessages.value || reachedStart.value,
);
// True once the oldest page has been reached for the current room; resets on
// room change. Drives the load-more control's end state and stops the
// auto-loader from spinning against an exhausted history.
const reachedStart = ref(false);
watch(
  () => chat.resolvedChatRoom?.id,
  () => {
    reachedStart.value = false;
  },
);
// Set when the user explicitly sends a message so the auto-scroll watcher
// still snaps to the bottom even if a load-previous fetch is in flight.
const justSentOwnMessage = ref(false);

const newMessage = ref("");
const messageContainerRef = useTemplateRef("messageContainer");
const scrollableParent = computed(() => {
  const element = messageContainerRef.value;
  if (!element) return null;

  return getClosestScrollableParent((element as ComponentPublicInstance).$el);
});

const isScrollingManually = ref(false);

onMounted(() => {
  if (props.loadMessagesOnMount && chat.resolvedChatRoom) {
    fetchingMoreMessages.value = true;
    chat.fetchMessagesForRoom(chat.resolvedChatRoom.id).finally(() => {
      scrollToBottom();
      fetchingMoreMessages.value = false;
    });
  }
});

function onReplyPress(messageId: string) {
  const message = chat.messages?.findById(messageId);

  if (!message) {
    console.error("Unable to find message to reply to");
    return;
  }

  replyToData.value = { message, replyToId: message.id };
  scrollIntoMessage(message.id);
}

function onMessageLike(messageId: string, isLiked: boolean) {
  emit("messageLike", messageId, isLiked);
}

function scrollIntoMessage(messageId: string): void {
  const element = document.querySelector(`[data-message="${messageId}"]`);
  if (!element) {
    console.error("Unable to find element to scroll to");
    return;
  }
  element.scrollIntoView({
    behavior: "smooth",
    block: "center",
  });
}

function sendMessage() {
  if (newMessage.value === "") return;

  emit(
    "sendMessage",
    newMessage.value.trim(),
    showQuestions.value,
    replyToData.value?.replyToId ?? null,
  );

  if (showQuestions.value) showQuestions.value = false;

  newMessage.value = "";
  replyToData.value = null;
  justSentOwnMessage.value = true;
  scrollToBottom();
}

function onDeleteMessage(messageId: string) {
  deleteMessageConfirmationModalOpen.value = true;
  toDeleteMessageId.value = messageId;
}

async function togglePinMessage(messageId: string, value: boolean) {
  await chat.setMessagePinnedState(messageId, value);
}

async function deleteMessage() {
  if (toDeleteMessageId.value === null) return;

  await chat.deleteChatMessage(toDeleteMessageId.value);
  toDeleteMessageId.value = null;
  deleteMessageConfirmationModalOpen.value = false;
}

watchEffect((onCleanup) => {
  const element = scrollableParent.value;
  if (!element) return;

  function onScroll() {
    if (!element) return;

    const threshold = 300;
    const currentScroll = element.scrollTop + element.clientHeight + threshold;
    const shouldAutoScroll = currentScroll >= element.scrollHeight;

    isScrollingManually.value = !shouldAutoScroll;
  }

  element.addEventListener("scroll", onScroll);
  onCleanup(() => {
    element.removeEventListener("scroll", onScroll);
  });
});

// Loads the previous page and keeps the user's read position stable. The
// scroll-anchor mechanics live in the shared `captureScrollAnchor` util; this
// only owns the fetch + the in-flight flag.
async function loadPreviousMessages() {
  if (
    fetchingMoreMessages.value ||
    !chat.resolvedChatRoom ||
    !hasOlderMessages.value
  )
    return;
  fetchingMoreMessages.value = true;
  const restoreScrollAnchor = captureScrollAnchor(scrollableParent.value);
  try {
    const msgs = await chat.fetchMessagesForRoom(chat.resolvedChatRoom.id);
    if (msgs && msgs.length > 0) {
      await nextTick();
      await restoreScrollAnchor();
      if (!hasOlderMessages.value) {
        reachedStart.value = true;
      }
    } else {
      // null/empty => the store has no older page for this room.
      reachedStart.value = true;
    }
  } finally {
    fetchingMoreMessages.value = false;
  }
}

// Production-grade infinite loader: a throttled scroll-position handler on the
// scroll surface. IntersectionObserver was tried but vue-virtual-scroller does
// not produce IO transitions for an element root (only the initial observation
// is delivered), so an IO would silently never re-fire. A scroll handler
// re-evaluates the condition FRESH on every scroll tick, so — unlike the old
// `useScroll().arrivedState.top` watcher that only reacted to a false→true
// edge and got stuck whenever the post-load anchor restore left the user still
// inside the trigger zone — it cannot wedge: it fires whenever scrollTop is
// within PREFETCH_PX of the top and a load isn't already running. The rAF gate
// coalesces the scroll burst.
const PREFETCH_PX = 600;
watch(
  [scrollableParent, () => chat.resolvedChatRoom?.id],
  ([root], _old, onCleanup) => {
    if (!root) return;
    let ticking = false;
    const maybeLoad = () => {
      ticking = false;
      if (
        root.scrollTop <= PREFETCH_PX &&
        !fetchingMoreMessages.value &&
        !reachedStart.value &&
        hasOlderMessages.value &&
        messages.value.length > 0 &&
        chat.resolvedChatRoom &&
        !replyToData.value?.replyToId
      ) {
        void loadPreviousMessages();
      }
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(maybeLoad);
    };
    root.addEventListener("scroll", onScroll, { passive: true });
    // Evaluate once on (re)bind: the list may already be short enough that the
    // top is in range without any scroll event ever firing.
    onScroll();
    onCleanup(() => root.removeEventListener("scroll", onScroll));
  },
  { immediate: true },
);

watch(
  () => chat.lastMessage,
  useDebounceFn(async (last: IChatMessage | null) => {
    const replyingToMessage = replyToData.value?.replyToId ?? null;
    const isReplyingToSameMessage = replyingToMessage === last?.replyTo?.id;

    if (replyingToMessage) {
      await nextTick();

      if (isReplyingToSameMessage && last) {
        scrollIntoMessage(last.id);
      } else {
        scrollIntoMessage(replyingToMessage);
      }
    } else if (
      // Don't auto-scroll while loading previous messages (host jump-to-bottom)
      // or while the user is scrolling manually — UNLESS the user just sent a
      // message, in which case snapping to their own message wins.
      justSentOwnMessage.value ||
      (!fetchingMoreMessages.value && !isScrollingManually.value)
    ) {
      justSentOwnMessage.value = false;
      await nextTick();
      scrollToBottom();
    }
  }, 150),
  { immediate: true },
);

function scrollToBottom(_behavior: ScrollBehavior = "smooth") {
  messageContainerRef.value?.scrollToBottom();
}

function onSetMessagePinnedState(messageId: string, value: boolean) {
  togglePinMessage(messageId, value);
}
</script>
